import { useEffect, useState } from "react";
import api, { formatIDR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { sku: "", name: "", description: "", unit: "pcs", price: 0, cost: 0, stock: 0, min_stock: 0, image_url: "" };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/products").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...form, price: +form.price, cost: +form.cost, stock: +form.stock, min_stock: +form.min_stock };
      if (editing) await api.put(`/products/${editing.id}`, body);
      else await api.post("/products", body);
      toast.success("Tersimpan"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch { toast.error("Gagal menyimpan"); }
  };
  const remove = async (id) => { if(!confirm("Hapus produk ini?")) return; await api.delete(`/products/${id}`); load(); };
  const edit = (it) => { setEditing(it); setForm({...empty, ...it}); setOpen(true); };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const token = localStorage.getItem("token");
      setForm(f => ({...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}?auth=${token}`}));
      toast.success("Gambar terupload");
    } catch { toast.error("Upload gagal"); } finally { setUploading(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Master Data</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Produk & Stok</h1>
          <p className="text-sm text-forest-400 mt-2">Kelola daftar produk, harga jual, harga pokok, dan stok.</p>
        </div>
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setEditing(null); setForm(empty);}}}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-product" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><Plus size={16}/> Tambah Produk</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing?"Ubah":"Tambah"} Produk</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input data-testid="inp-product-sku" value={form.sku} onChange={e=>setForm({...form, sku:e.target.value})}/></div>
              <div><Label>Nama</Label><Input data-testid="inp-product-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
              <div><Label>Satuan</Label><Input value={form.unit} onChange={e=>setForm({...form, unit:e.target.value})}/></div>
              <div><Label>Stok Minimum</Label><Input type="number" value={form.min_stock} onChange={e=>setForm({...form, min_stock:e.target.value})}/></div>
              <div><Label>Harga Jual (IDR)</Label><Input data-testid="inp-product-price" type="number" value={form.price} onChange={e=>setForm({...form, price:e.target.value})}/></div>
              <div><Label>Harga Pokok (IDR)</Label><Input type="number" value={form.cost} onChange={e=>setForm({...form, cost:e.target.value})}/></div>
              <div><Label>Stok Saat Ini</Label><Input type="number" value={form.stock} onChange={e=>setForm({...form, stock:e.target.value})}/></div>
              <div><Label>Foto</Label>
                <label className="flex items-center gap-2 border border-forest-100 px-3 py-2 rounded-sm cursor-pointer text-sm">
                  <Upload size={14}/> {uploading?"Mengupload…":"Pilih gambar"}
                  <input type="file" accept="image/*" hidden onChange={e=>e.target.files[0]&&uploadImage(e.target.files[0])}/>
                </label>
              </div>
              <div className="col-span-2"><Label>Deskripsi</Label><Textarea value={form.description||""} onChange={e=>setForm({...form, description:e.target.value})}/></div>
              {form.image_url && <div className="col-span-2"><img alt="preview" src={form.image_url} className="h-24 rounded-sm border border-forest-100"/></div>}
            </div>
            <DialogFooter><Button data-testid="btn-save-product" onClick={save} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-forest-100">
              <th className="text-left px-4 py-3 label-tag">SKU</th>
              <th className="text-left px-4 py-3 label-tag">Produk</th>
              <th className="text-right px-4 py-3 label-tag">Harga</th>
              <th className="text-right px-4 py-3 label-tag">Stok</th>
              <th className="text-left px-4 py-3 label-tag">Status</th>
              <th className="text-right px-4 py-3 label-tag">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={6} className="text-center py-12 text-forest-400">Belum ada produk</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="data-row border-b border-forest-100">
                <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                <td className="px-4 py-3 font-medium">{p.name} <span className="text-forest-400 text-xs">· {p.unit}</span></td>
                <td className="px-4 py-3 text-right num">{formatIDR(p.price)}</td>
                <td className="px-4 py-3 text-right num">{p.stock}</td>
                <td className="px-4 py-3">{p.stock < p.min_stock ? <Badge className="bg-[#C45A3D] text-white rounded-sm">Stok Rendah</Badge> : <Badge variant="outline" className="rounded-sm">Aman</Badge>}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={()=>edit(p)} className="p-1.5 hover:bg-forest-50"><Pencil size={14}/></button>
                  <button onClick={()=>remove(p.id)} className="p-1.5 hover:bg-forest-50 text-[#C45A3D]"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
